import React, { useState } from 'react'
import {
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    makeStyles,
    MenuItem,
    Modal,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import GetAppIcon from '@material-ui/icons/GetApp'
import { Pagination } from '@material-ui/lab'
import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

import { toast } from 'react-toastify'

import { Container } from '../../../../components'
import { axiosInstance } from '../../../../helpers/axios'
import { typographyStyle } from '../../../../theme/typographyStyle'

const PROJECT_KEY_ANALYTICS_SORT = {
    ApartmentNameASC: 'ApartmentNameASC',
    ApartmentNameDESC: 'ApartmentNameDESC',
    ProjectNameASC: 'ProjectNameASC',
    ProjectNameDESC: 'ProjectNameDESC',
    TotalKeysASC: 'TotalKeysASC',
    TotalKeysDESC: 'TotalKeysDESC',
    KeysTurnedInASC: 'KeysTurnedInASC',
    KeysTurnedInDESC: 'KeysTurnedInDESC',
    KeysNotTurnedInASC: 'KeysNotTurnedInASC',
    KeysNotTurnedInDESC: 'KeysNotTurnedInDESC',
} as const

type ProjectKeyAnalyticsSort = typeof PROJECT_KEY_ANALYTICS_SORT[keyof typeof PROJECT_KEY_ANALYTICS_SORT]

interface ProjectKeyAnalytics {
    id: number
    apartment_name: string
    name: string
    total_keys_count: number
    keys_turned_in_count: number
    keys_not_turned_in_count: number
    partial_units_count: number
    fully_vacant_units_count: number
    partial_units_keys_turned_in_count: number
    fully_vacant_units_keys_turned_in_count: number
}

interface PaginatedProjectKeyAnalyticsResponse {
    results: ProjectKeyAnalytics[]
    count: number
}

interface ProjectKeyAnalyticsHeader {
    field: keyof ProjectKeyAnalytics
    display: string
    sortAsc?: ProjectKeyAnalyticsSort
    sortDesc?: ProjectKeyAnalyticsSort
}

const projectKeyAnalyticsHeaders: ProjectKeyAnalyticsHeader[] = [
    {
        field: 'apartment_name',
        display: 'Property',
        sortAsc: PROJECT_KEY_ANALYTICS_SORT.ApartmentNameASC,
        sortDesc: PROJECT_KEY_ANALYTICS_SORT.ApartmentNameDESC,
    },
    {
        field: 'name',
        display: 'Project',
        sortAsc: PROJECT_KEY_ANALYTICS_SORT.ProjectNameASC,
        sortDesc: PROJECT_KEY_ANALYTICS_SORT.ProjectNameDESC,
    },
    {
        field: 'total_keys_count',
        display: 'Total Keys',
        sortAsc: PROJECT_KEY_ANALYTICS_SORT.TotalKeysASC,
        sortDesc: PROJECT_KEY_ANALYTICS_SORT.TotalKeysDESC,
    },
    {
        field: 'keys_turned_in_count',
        display: 'Keys Turned In',
        sortAsc: PROJECT_KEY_ANALYTICS_SORT.KeysTurnedInASC,
        sortDesc: PROJECT_KEY_ANALYTICS_SORT.KeysTurnedInDESC,
    },
    {
        field: 'keys_not_turned_in_count',
        display: 'Keys Not Turned In',
        sortAsc: PROJECT_KEY_ANALYTICS_SORT.KeysNotTurnedInASC,
        sortDesc: PROJECT_KEY_ANALYTICS_SORT.KeysNotTurnedInDESC,
    },
    {
        field: 'partial_units_count',
        display: 'Partial Units',
    },
    {
        field: 'fully_vacant_units_count',
        display: 'Fully Vacant Units',
    },
    {
        field: 'partial_units_keys_turned_in_count',
        display: 'Partial Units All Keys In',
    },
    {
        field: 'fully_vacant_units_keys_turned_in_count',
        display: 'Fully Vacant Units All Keys In',
    },
]

interface Props {
    open: boolean
    onClose: () => void
    workspaceId?: number
    organizationId?: number
    archived: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    tableHeader: {
        '& th': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontWeight: 600,
            position: 'sticky',
            top: 0,
            zIndex: 2,
            whiteSpace: 'wrap',
        },
    },
    cell: {
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'center',
        fontSize: '0.875rem',
    },
    sortLabel: {
        color: theme.palette.common.white,
        '&:hover': {
            color: theme.palette.common.white,
        },
        '&.MuiTableSortLabel-active': {
            color: theme.palette.common.white,
        },
        '& .MuiTableSortLabel-icon': {
            color: theme.palette.common.white + ' !important',
        },
    },
}))

export const ProjectKeyAnalyticsModal = (props: Props) => {
    const { open, onClose, workspaceId, organizationId, archived } = props

    const classes = useStyles()
    const theme = useTheme()

    const [pageSize, setPageSize] = useState(25)
    const [page, setPage] = useState(1)
    const [sort, setSort] = useState<ProjectKeyAnalyticsSort>(
        PROJECT_KEY_ANALYTICS_SORT.ProjectNameASC,
    )
    const [isGeneratingReport, setIsGeneratingReport] = useState(false)

    const offset = (page - 1) * pageSize

    const { data, isLoading } = useQuery<
        AxiosResponse<PaginatedProjectKeyAnalyticsResponse>,
        Error
    >({
        queryKey: [
            'project-area-status-analytics',
            workspaceId,
            organizationId,
            archived,
            pageSize,
            offset,
            sort,
        ],
        queryFn: () =>
            axiosInstance.get(
                '/workorder/org-project-analytics/project-area-status-analytics/',
                {
                    params: {
                        limit: pageSize,
                        offset,
                        sort,
                        archived,
                        ...(workspaceId
                            ? { workspace_id: workspaceId }
                            : organizationId
                            ? { organization_id: organizationId }
                            : {}),
                    },
                },
            ),
        enabled: open,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    const results = data?.data.results ?? []
    const totalCount = data?.data.count ?? 0
    const numPages = Math.ceil(totalCount / pageSize)
    const start = totalCount === 0 ? 0 : offset + 1
    const end = Math.min(offset + pageSize, totalCount)

    const handleSort = (header: ProjectKeyAnalyticsHeader) => {
        if (header.sortAsc === undefined || header.sortDesc === undefined) {
            return
        }

        setSort(sort === header.sortAsc ? header.sortDesc : header.sortAsc)
        setPage(1)
    }

    const getTextAlign = (field: keyof ProjectKeyAnalytics) => {
        return field === 'name' || field === 'apartment_name'
            ? 'left'
            : 'center'
    }

    const handleGenerateReport = async () => {
        setIsGeneratingReport(true)
        try {
            const response = await axiosInstance.get(
                '/workorder/org-project-analytics/generate-area-status-report/',
                {
                    params: {
                        archived,
                    },
                },
            )
            if (response.status === 200) {
                toast.success(
                    'Report is being generated and will be emailed to you shortly.',
                )
            } else {
                toast.error('Failed to generate report')
            }
        } catch {
            toast.error('Failed to generate report')
        } finally {
            setIsGeneratingReport(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '1400px',
                    maxWidth: 'calc(100vw - 80px)',
                    height: '720px',
                    maxHeight: 'calc(100vh - 80px)',
                    padding: theme.spacing(2),
                    outline: 'none',
                }}
            >
                <Container
                    style={{
                        alignItems: 'center',
                        marginBottom: theme.spacing(2),
                    }}
                >
                    <Typography
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 700,
                            flex: 1,
                        }}
                    >
                        Key Tracking Analytics
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                            isGeneratingReport ? (
                                <CircularProgress size={16} />
                            ) : (
                                <GetAppIcon />
                            )
                        }
                        onClick={handleGenerateReport}
                        disabled={isGeneratingReport}
                        style={{ marginRight: theme.spacing(1) }}
                    >
                        Email report
                    </Button>
                    <Tooltip title="Close">
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Container>

                <Container
                    style={{
                        overflowX: 'auto',
                        flex: 1,
                    }}
                >
                    <Table
                        stickyHeader
                        size="small"
                        style={{ minWidth: '1350px', tableLayout: 'auto' }}
                    >
                        <TableHead>
                            <TableRow className={classes.tableHeader}>
                                {projectKeyAnalyticsHeaders.map((header) => (
                                    <TableCell
                                        key={header.field}
                                        className={classes.cell}
                                        style={{
                                            textAlign: getTextAlign(
                                                header.field,
                                            ),
                                            width:
                                                header.field === 'name'
                                                    ? 320
                                                    : undefined,
                                            whiteSpace:
                                                header.field === 'name'
                                                    ? 'normal'
                                                    : undefined,
                                        }}
                                    >
                                        {header.sortAsc && header.sortDesc ? (
                                            <TableSortLabel
                                                className={classes.sortLabel}
                                                active={
                                                    sort === header.sortAsc ||
                                                    sort === header.sortDesc
                                                }
                                                direction={
                                                    sort === header.sortAsc
                                                        ? 'asc'
                                                        : 'desc'
                                                }
                                                onClick={() =>
                                                    handleSort(header)
                                                }
                                            >
                                                {header.display}
                                            </TableSortLabel>
                                        ) : (
                                            <span>{header.display}</span>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={
                                            projectKeyAnalyticsHeaders.length
                                        }
                                        style={{
                                            height: 360,
                                            textAlign: 'center',
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : results.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={
                                            projectKeyAnalyticsHeaders.length
                                        }
                                    >
                                        No key analytics found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                results.map((project) => (
                                    <TableRow key={project.id}>
                                        {projectKeyAnalyticsHeaders.map(
                                            (header) => (
                                                <TableCell
                                                    key={header.field}
                                                    className={classes.cell}
                                                    style={{
                                                        textAlign: getTextAlign(
                                                            header.field,
                                                        ),
                                                        width:
                                                            header.field ===
                                                            'name'
                                                                ? 320
                                                                : undefined,
                                                        whiteSpace:
                                                            header.field ===
                                                            'name'
                                                                ? 'normal'
                                                                : undefined,
                                                        overflow:
                                                            header.field ===
                                                            'name'
                                                                ? 'visible'
                                                                : undefined,
                                                    }}
                                                    title={String(
                                                        project[header.field],
                                                    )}
                                                >
                                                    {project[header.field]}
                                                </TableCell>
                                            ),
                                        )}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Container>

                <Container
                    style={{
                        ...typographyStyle(theme.typography.subtitle1),
                        borderTop: `1px solid ${theme.palette.grey[400]}`,
                        padding: theme.spacing(1),
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Container
                        style={{ alignItems: 'center', gap: theme.spacing(1) }}
                    >
                        <span>Rows per page:</span>
                        <FormControl size="small" style={{ minWidth: 80 }}>
                            <Select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(e.target.value as number)
                                    setPage(1)
                                }}
                                displayEmpty
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                        </FormControl>
                    </Container>

                    <Container
                        style={{ alignItems: 'center', gap: theme.spacing(2) }}
                    >
                        <span>
                            {start} - {end} of {totalCount}
                        </span>
                        <Pagination
                            count={numPages}
                            page={page}
                            onChange={(_: unknown, newPage: number) => {
                                setPage(newPage)
                            }}
                            size="small"
                        />
                    </Container>
                </Container>
            </Paper>
        </Modal>
    )
}
